import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import {
  Box, List, ListItemButton, ListItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography
} from "@mui/material";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LoadingModal from "../../components/LoadingModal";
import { useAppDispatch } from "../../redux/store";
import AdminPageFrame from "../components/AdminPageFrame";
import ContainerPaper from "../components/ContainerPaper";
import DropDown from "../components/DropDown";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import Loading from "../components/Loading";
import ReadMore from "../components/ReadMore";
import FetchButton from "../components/FetchButton";
import ImageDialog from "../components/samples/ImageDialog";
import SampleImageCard from "../components/samples/SampleImageCard";
import { setErrorObj } from "../redux/errorReducer";
import apiServices from "../services/apiServices";
import { isAdmin } from "../util/util";

const perPage = 12;

type ActionTarget = {
  open: boolean;
  fileInfo: any;
}
type SortOption = {
  value: string;
  name: string;
}

export default function SampleImageList() {
  const dispatch = useAppDispatch();
  const { admin } = useSelector((state: any) => state.adminAuth);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  /* 検索条件 */
  const [term, setTerm] = useState<string>("");
  const sortOptions: SortOption[] = [
    {value: "name_asc", name: T("mng_sample_images_sort_by_name_asc")},
    {value: "name_desc", name: T("mng_sample_images_sort_by_name_desc")},
  ]
  const [sort, setSort] = useState("name_asc");

  const [searchCondition, setSearchCondition] = useState<{
    term: string;
    sortOption: SortOption;
  }>({ term: term, sortOption: sortOptions[0] });

  /* 画像一覧 */
  const [imageList, setImageList] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({});

  const [imageToUpdate, setImageToUpdate] = useState<ActionTarget>({ open: false, fileInfo: undefined });
  const [imageToDelete, setImageToDelete] = useState<ActionTarget>({ open: false, fileInfo: undefined });

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialize = async () => {
    await getSampleImageList(term, sort);
    setIsLoading(false);
  }

  const getSampleImageList = async (q: string, s: string, page?: number) => {
    const _sortOption = sortOptions.find((item:SortOption) => item.value === s)!;
    const [ type, direction ] = _sortOption.value.split("_");
    const _term = q;
    try {
      const res = await apiServices.getSampleImageList({
        per_page: perPage,
        sort_type: type,
        sort_direction: direction,
        term: _term,
        page: page
      });
      if (page) {
        setImageList((prev: any[]) => [...prev, ...res.samples]);
      } else {
        setImageList(res.samples);
      }
      setPagination(res.pagination);
      setSearchCondition({ term: _term, sortOption: _sortOption })
    } catch (error: any) {
      dispatch(setErrorObj(error));
    }
  }

  /* イベント */
  const handleSearch = async () => {
    setIsSearching(true);
    await getSampleImageList(term, sort);
    setIsSearching(false);

    return false
  }

  const handleMore = async () => {
    if (!pagination.next_page || pagination.next_page < 1) return

    setIsSearching(true);
    await getSampleImageList(searchCondition.term, searchCondition.sortOption.value, pagination.next_page);
    setIsSearching(false);
  };

  const handleSubmit = async (file: File, orgFileInfo?: any) => {
    setIsLoading(true);
    try {
      if (orgFileInfo) {
        await apiServices.updateSampleImage(orgFileInfo.id, file);
      } else {
        await apiServices.createSampleImage(file);
      }
    } catch (error: any) {
      dispatch(setErrorObj(error));
      setIsLoading(false);
      return false;
    }

    // 最新データ読込
    setImageList([]); // 更新した画像を再読み込みさせるためいったん空にする
    await getSampleImageList(searchCondition.term, searchCondition.sortOption.value);

    setImageToUpdate({ open: false, fileInfo: undefined });
    setIsLoading(false);
    return true;
  }

  const handleDelete = async () => {
    if (!imageToDelete?.fileInfo?.id) return false

    setIsLoading(true);
    try {
      // 削除
      await apiServices.deleteSampleImage(imageToDelete.fileInfo.id);
    } catch (error: any) {
      dispatch(setErrorObj(error));
      setIsLoading(false);
      return false;
    }

    // 最新データ読込
    await getSampleImageList(searchCondition.term, searchCondition.sortOption.value);

    setIsLoading(false);
    setImageToDelete({ open: false, fileInfo: undefined });
    return false;
  }

  return (
    <AdminPageFrame>
      <LoadingModal visible={isLoading}/>

      <ContainerPaper>
        <Typography sx={styles.searchTitle}>{T("mng_search_area_title")}</Typography>
        <Box sx={styles.searchContainer}>
          <TextField
            placeholder={`${T("mng_sample_images_search_term")}`}
            variant="outlined"
            value={term}
            onChange={(e: any) => setTerm(e.target.value)}
            sx={styles.searchQuery}
          />
          <DropDown 
            title={T("mng_sort")}
            value={sort}
            options={sortOptions}
            onChange={(e: any) => setSort(e.target.value)}
          />
          <FetchButton
            title={T('mng_search')}
            onSubmit={handleSearch}
            validate={() => Promise.resolve(true)}
          />
        </Box>
      </ContainerPaper>

      <ContainerPaper sx={styles.resultContainer}>
        <Loading visible={isSearching} />
        <Table aria-label="caption table">
          <TableHead>
            <TableRow>
              <TableCell>{T("mng_data_definitions_sample_images")}</TableCell>
              <TableCell align="right">
                {isAdmin(admin) && (
                  <>
                    <AddPhotoAlternateIcon onClick={() => setImageToUpdate({ open: true, fileInfo: undefined })} />
                    <Typography component="span" onClick={() => setImageToUpdate({ open: true, fileInfo: undefined })}>
                      {T("mng_sample_images_create")}
                    </Typography>
                  </>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={2}>
                <List sx={{ display: "flex", flexWrap: "wrap"}}>
                  {imageList.map((image: any, i: number) => (
                    <ListItem style={styles.imageContainer} key={i}>
                      <ListItemButton disableGutters>
                        <SampleImageCard
                          fileInfo={image}
                          onClick={() => setImageToUpdate({ open: true, fileInfo: image })}
                          onDeleteClick={() => setImageToDelete({ open: true, fileInfo: image })}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <ReadMore
          open={pagination.next_page > 0}
          onClick={handleMore} 
        />
      </ContainerPaper>

      {imageToUpdate.open &&
        <ImageDialog
          fileInfo={imageToUpdate.fileInfo}
          onSubmit={handleSubmit}
          onClose={() => setImageToUpdate({ open: false, fileInfo: undefined })}
          onError={(message: string) => dispatch(setErrorObj(message))}
        />
      }

      <DeleteConfirmationDialog
        open={imageToDelete.open}
        message={T("mng_sample_images_delete_confirmation_message")}
        validate={async () => true}
        onSubmit={handleDelete}
        onClose={() => setImageToDelete({ open: false, fileInfo: undefined })}
      />
    </AdminPageFrame>
  );
};

const styles = {
  searchTitle: {
    paddingY: 2,
  },
  searchContainer: {
    display: "flex",
    gap: 2
  },
  searchQuery: {
    flex: 1
  },
  resultContainer: {
    position: "relative"
  },
  imageContainer: {
    maxWidth: '330px',
    padding: '2px',
    position: 'relative' as 'relative',
    top: 0,
    right: 0,
    boxSizing: 'border-box' as 'border-box'
  },
}
